import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../../../../../../api';
import Pagination from '../../../../Tools/Pagination';

const EquipmentChecklistHistoryModal = ({ equipmentId, onClose, refreshTrigger }) => {
  const [historyItems, setHistoryItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [submittedBy, setSubmittedBy] = useState('Unknown');
  const itemsPerPage = 10;

  // Utility function to capitalize each word in a user's name
  const capitalizeName = (name) => {
    if (!name) return 'Unknown';
    return name
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Fetch checklist history data from API
  const fetchChecklistHistory = useCallback(async () => {
    const currentEquipmentId = sessionStorage.getItem('currentEquipmentId');
    const currentSubmissionId = sessionStorage.getItem('currentSubmissionId');
    
    if (!currentEquipmentId || !currentSubmissionId) {
      console.warn('Missing equipment ID or submission ID');
      return;
    }

    setIsLoading(true);
    try {
      const response = await axiosInstance.get(`/ems/view-equipment-history/${currentEquipmentId}/${currentSubmissionId}`, {
        headers: {
          'Accept': 'application/vnd.api+json',
          'Content-Type': 'application/vnd.api+json',
        },
      });
      
      if (response.data && response.data.data) {
        // Format the response data for display
        const formattedItems = response.data.data
          .filter(item => item.checklist_item_id !== null || item.status !== null)
          .map(item => {
            const emsItem = item.ems_cheklist_item;
            return {
              id: item.id,
              name: emsItem?.name || 'Unknown Item',
              criteria: item.criteria || emsItem?.criteria || '',
              status: item.status,
              notes: item.notes || '',
              submittedAt: item.submitted_at,
              user: item.user,
            };
          });

        // Sort items by ID in ascending order
        formattedItems.sort((a, b) => a.id - b.id);
        setHistoryItems(formattedItems);
        
        // Set submitted by information
        if (formattedItems.length > 0 && formattedItems[0].user) {
          const user = formattedItems[0].user;
          setSubmittedBy(capitalizeName(`${user.first_name} ${user.last_name}`));
        }
        
        setCurrentPage(1);
      }
    } catch (error) {
      console.error('Error fetching checklist history:', error);
      alert('Failed to load checklist history');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Effect to handle modal open and fetch data
  useEffect(() => {
    const modalEl = document.getElementById('bs-equipment-checklist-history');
    if (!modalEl) return;

    const handleModalOpen = () => {
      fetchChecklistHistory();
    };

    const handleModalClose = () => {
      sessionStorage.removeItem('currentEquipmentId');
      sessionStorage.removeItem('currentSubmissionId');
      setHistoryItems([]);
      setSubmittedBy('Unknown');
      if (onClose) onClose();
    };

    modalEl.addEventListener('shown.bs.modal', handleModalOpen);
    modalEl.addEventListener('hidden.bs.modal', handleModalClose);

    return () => {
      modalEl.removeEventListener('shown.bs.modal', handleModalOpen);
      modalEl.removeEventListener('hidden.bs.modal', handleModalClose);
    };
  }, [fetchChecklistHistory, onClose]);

  // Effect to show modal when equipmentId changes
  useEffect(() => {
    if (equipmentId) {
      const modalEl = document.getElementById('bs-equipment-checklist-history');
      if (modalEl && window.bootstrap?.Modal) {
        const modal = window.bootstrap.Modal.getOrCreateInstance(modalEl);
        modal.show();
      }
    }
  }, [equipmentId]);

  // Effect to refresh data when refreshTrigger changes
  useEffect(() => {
    if (equipmentId) {
      fetchChecklistHistory();
    }
  }, [refreshTrigger, equipmentId, fetchChecklistHistory]);

  // Pagination calculation
  const totalItems = historyItems.length;
  const paginatedItems = historyItems.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Render checklist history items
  const renderHistoryItems = (items) =>
    items.map((item, index) => {
      const serialNumber = (currentPage - 1) * itemsPerPage + index + 1;
      return (
        <tr key={`history-${item.id}`}>
          <td>{serialNumber}</td>
          <td><p className="mb-0 fw-normal">{item.name}</p></td>
          <td>
            <input 
              type="text" 
              className="form-control" 
              value={item.criteria} 
              readOnly 
            />
          </td>
          <td className="text-center">
            <input
              type="checkbox"
              className="form-check-input"
              checked={item.status === 1}
              disabled
            />
          </td>
        </tr>
      );
    });

  return (
    <div
      id="bs-equipment-checklist-history"
      className="modal fade"
      tabIndex="-1"
      aria-labelledby="bs-equipment-checklist-history"
      aria-hidden="true"
    >
      <div className="modal-dialog modal-dialog-scrollable modal-xl">
        <div className="modal-content">
          <div className="modal-header d-flex align-items-center">
            <h5 className="modal-title">Equipment Checklist History</h5>
            <button 
              type="button" 
              className="btn-close" 
              data-bs-dismiss="modal" 
              aria-label="Close" 
            />
          </div>
          <div className="modal-body">
            <div className="text-muted pb-3 text-center">
              Submitted by: <strong>{submittedBy}</strong>
            </div>
            {isLoading ? (
              <div className="text-center py-4">Loading History...</div>
            ) : (
              <div className="row">
                <div className="col-md-12">
                  <div className="table-responsive">
                    <table className="table text-nowrap customize-table mb-0 align-middle">
                      <thead className="text-dark">
                        <tr>
                          <th><h6 className="fw-semibold mb-0">ID</h6></th>
                          <th><h6 className="fw-semibold mb-0">Item</h6></th>
                          <th><h6 className="fw-semibold mb-0">Criteria</h6></th>
                          <th className="text-center">
                            <h6 className="fw-semibold mb-0">Status</h6>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {historyItems.length > 0 ? (
                          renderHistoryItems(paginatedItems)
                        ) : (
                          <tr>
                            <td colSpan="4" className="text-center py-4">
                              No history found for this equipment
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
                {historyItems.length > itemsPerPage && (
                  <div className="mt-3">
                    <Pagination
                      total={totalItems}
                      limit={itemsPerPage}
                      page={currentPage}
                      onPageChange={setCurrentPage}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-outline-danger"
              data-bs-dismiss="modal"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EquipmentChecklistHistoryModal;